import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { v4 as uuidv4 } from 'uuid';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { consentRoleLabel, isNonSigningSignerType } from 'libs.consent.common';
import { Spinner, useLocalizeMessage } from 'libs.nucleus';
import { ConsentElementOverlay } from './consent_element_overlay.component';
import { ConsentElementToolbar } from './consent_element_toolbar.component';
import { ConsentPlacementInspector } from './consent_placement_inspector.component';
import { buildChoiceOptions, CONSENT_ELEMENT_TYPES, initialChoiceOptionSpecs, isChoiceElementType, } from '../../constants/consent_element_types.constants';
import { roleSwatchColor } from '../../constants/consent_placement_ui.constants';
import { useConsentContext } from '../../hooks/use_consent_context.hook';
const BASE_URL = `//unpkg.com/pdfjs-dist@${pdfjs.version}`;
pdfjs.GlobalWorkerOptions.workerSrc = `${BASE_URL}/build/pdf.worker.min.mjs`;
const pdfOptions = {
    cMapUrl: `https:${BASE_URL}/cmaps/`,
    standardFontDataUrl: `https:${BASE_URL}/standard_fonts/`,
};
// Pages scale to the viewer up to this width; overlays are percentage-positioned, so they follow.
const PAGE_MAX_WIDTH = 960;
const PAGE_GUTTER = 48;
// Only pages within this many viewport heights of the visible area mount a react-pdf canvas.
const MOUNT_VIEWPORTS = 2;
const US_LETTER_ASPECT = 11 / 8.5;
const downloadPdf = (pdfDataUrl, fileName) => {
    const link = document.createElement('a');
    link.href = pdfDataUrl;
    link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    link.click();
};
export const ConsentPdfEditorContent = ({ document, readOnly = false }) => {
    const { addElement, updateElement, deleteElement, updateDocument, selectedElement, setSelectedElement } = useConsentContext();
    const translate = useLocalizeMessage();
    const [announcement, setAnnouncement] = useState('');
    const [focusedOverlayItemId, setFocusedOverlayItemId] = useState(null);
    const [numPages, setNumPages] = useState(document.totalPages || 0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageDraft, setPageDraft] = useState('1');
    const pageInputFocused = useRef(false);
    const [mountedPages, setMountedPages] = useState(() => new Set([1]));
    const [containerWidth, setContainerWidth] = useState(0);
    const [pageAspect, setPageAspect] = useState(US_LETTER_ASPECT);
    const scrollRef = useRef(null);
    const pageRefs = useRef(new Map());
    const [activePlacementType, setActivePlacementType] = useState(null);
    const onDocumentLoadSuccess = useCallback(({ numPages: pages }) => {
        setNumPages(pages);
        if (!readOnly && document.totalPages !== pages) {
            void updateDocument({ ...document, totalPages: pages });
        }
    }, [document, readOnly, updateDocument]);
    const updateViewport = useCallback(() => {
        const scroller = scrollRef.current;
        // A zero-height scroller would drop every page. The first layout pass can report 0 until the
        // flex parent has a height, which is also when a window resize makes the PDF appear.
        if (!scroller || scroller.clientHeight === 0) {
            return;
        }
        const top = scroller.scrollTop;
        const height = scroller.clientHeight;
        // The first pass often runs before the page boxes exist, or while the flex viewer still
        // has no height. An empty result then unmounts page 1, and nothing asks again until resize.
        if (height === 0 || pageRefs.current.size === 0) {
            return;
        }
        const middle = top + height / 2;
        let inView = 1;
        const near = new Set();
        pageRefs.current.forEach((node, page) => {
            const start = node.offsetTop;
            if (start <= middle && page > inView) {
                inView = page;
            }
            if (start + node.offsetHeight >= top - height * MOUNT_VIEWPORTS &&
                start <= top + height * (MOUNT_VIEWPORTS + 1)) {
                near.add(page);
            }
        });
        setCurrentPage(inView);
        setMountedPages((prev) => (prev.size === near.size && [...near].every((page) => prev.has(page)) ? prev : near));
    }, []);
    useLayoutEffect(() => {
        const scroller = scrollRef.current;
        if (!scroller) {
            return;
        }
        // Measure before the canvas effect. A width that arrives after the first paint cancels the
        // in-flight pdf.js render, and that render leaves the canvas hidden until the next resize.
        const measure = () => {
            const width = scroller.clientWidth;
            if (width > 0) {
                setContainerWidth((current) => (current === width ? current : width));
            }
            updateViewport();
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(scroller);
        measure();
        // The modal's flex height is often still 0 in this effect and in the first observer record.
        const frame = requestAnimationFrame(measure);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [updateViewport]);
    // Placeholders change height once the width or page aspect is known; recompute which pages are near.
    useLayoutEffect(updateViewport, [updateViewport, numPages, containerWidth, pageAspect]);
    useEffect(() => {
        if (!pageInputFocused.current) {
            setPageDraft(String(currentPage));
        }
    }, [currentPage]);
    const scrollToPage = (page) => {
        pageRefs.current.get(page)?.scrollIntoView?.({ block: 'start' });
    };
    const commitPageJump = (raw) => {
        const page = Number(raw);
        if (!Number.isInteger(page)) {
            setPageDraft(String(currentPage));
            return;
        }
        const next = Math.min(Math.max(page, 1), totalPages);
        setPageDraft(String(next));
        scrollToPage(next);
    };
    const handlePlaceElement = (pageNumber, e) => {
        if (readOnly || !activePlacementType) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const def = CONSENT_ELEMENT_TYPES[activePlacementType];
        const xPct = Math.max(0, Math.min(1 - def.defaultWidth, (e.clientX - rect.left) / rect.width));
        const yPct = Math.max(0, Math.min(1 - def.defaultHeight, (e.clientY - rect.top) / rect.height));
        const elementId = `el-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        if (isChoiceElementType(activePlacementType)) {
            const preset = 'yn_default';
            const specs = initialChoiceOptionSpecs(activePlacementType, preset);
            const options = buildChoiceOptions(specs, pageNumber, { xPct, yPct });
            const newElement = {
                id: elementId,
                type: activePlacementType,
                page: pageNumber,
                position: { xPct, yPct, widthPct: def.defaultWidth, heightPct: def.defaultHeight },
                config: {
                    ec__key: uuidv4(),
                    ...(activePlacementType === 'SINGLE_SELECT' ? { ec__choice_preset: preset } : {}),
                    ec__allow_multiple: activePlacementType === 'MULTI_SELECT',
                    ec__options: options,
                },
            };
            addElement(document.id, newElement);
            setActivePlacementType(null);
            setSelectedElement(elementId);
            return;
        }
        const newElement = {
            id: elementId,
            type: activePlacementType,
            page: pageNumber,
            position: { xPct, yPct, widthPct: def.defaultWidth, heightPct: def.defaultHeight },
            config: { ec__key: uuidv4() },
        };
        addElement(document.id, newElement);
        setActivePlacementType(null);
        setSelectedElement(elementId);
    };
    const handleUpdateElement = (element) => {
        const previousPage = document.elements.find((el) => el.id === element.id)?.page;
        updateElement(document.id, element);
        // Page Up/Down moves an overlay to another page; follow it there.
        if (previousPage !== undefined && previousPage !== element.page) {
            scrollToPage(element.page);
        }
    };
    // A drag's listeners outlive the render that started it, and the marker itself remounts if the
    // element changes page mid-gesture, so geometry is merged onto the element as stored now.
    const latestElementsRef = useRef(document.elements);
    latestElementsRef.current = document.elements;
    const handleUpdateElementPosition = (elementId, position) => {
        const latest = latestElementsRef.current.find((el) => el.id === elementId);
        if (latest) {
            updateElement(document.id, { ...latest, position: { ...latest.position, ...position } });
        }
    };
    const handleDeleteElement = (elementId) => {
        deleteElement(document.id, elementId);
        if (selectedElement?.id === elementId) {
            setSelectedElement(null);
        }
    };
    const totalPages = Math.max(numPages, document.totalPages, 1);
    const pageWidth = containerWidth > 0 ? Math.min(PAGE_MAX_WIDTH, containerWidth - PAGE_GUTTER) : undefined;
    const signingRoles = document.roles.filter((role) => !isNonSigningSignerType(role.signerTypeId));
    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);
    const placeholderHeight = pageWidth ? pageWidth * pageAspect : undefined;
    return (_jsxs("div", { className: 'flex min-h-0 flex-1 flex-col gap-2 pt-3', "data-testid": 'consent-pdf-editor-content', "data-readonly": readOnly ? 'true' : 'false', children: [_jsx(ConsentElementToolbar, { activePlacementType: readOnly ? null : activePlacementType, onSelectType: setActivePlacementType, onCancelPlacement: () => setActivePlacementType(null), disabled: readOnly, onDownload: () => downloadPdf(document.pdfDataUrl, document.name), onPreviewAs: () => {
                    if (selectedElement) {
                        scrollToPage(selectedElement.page);
                    }
                }, statusRow: _jsxs(_Fragment, { children: [_jsx("span", { className: 'flex items-center gap-[13px]', "data-testid": 'consent-editor-role-legend', children: signingRoles.map((role) => (_jsxs("span", { className: 'flex items-center gap-[5px]', children: [_jsx("span", { className: 'inline-block h-[9px] w-[9px] rounded-[3px]', style: { backgroundColor: roleSwatchColor(role.signerTypeId) } }), _jsx("span", { className: 'text-[12.5px] text-neutral-900', children: consentRoleLabel(role) })] }, role.id))) }), _jsxs("label", { className: 'ml-auto flex items-center gap-1.5 text-xs text-neutral-500', "data-testid": 'consent-editor-page-jump', children: [translate('Page'), _jsx("input", { type: 'number', min: 1, max: totalPages, value: pageDraft, onFocus: () => {
                                        pageInputFocused.current = true;
                                    }, onBlur: () => {
                                        pageInputFocused.current = false;
                                        commitPageJump(pageDraft);
                                    }, onChange: (event) => setPageDraft(event.target.value), onKeyDown: (event) => {
                                        if (event.key === 'Enter') {
                                            commitPageJump(event.currentTarget.value);
                                        }
                                    }, "aria-label": translate('Go to page'), "data-testid": 'consent-editor-page-input', className: 'w-12 rounded border border-neutral-200 px-1 py-0.5 text-center text-xs text-neutral-900' }), translate('of {total}', { total: String(totalPages) })] })] }) }), _jsxs("div", { className: 'flex min-h-0 flex-1 gap-3.5', children: [_jsxs("div", { ref: scrollRef, onScroll: updateViewport, className: 'relative flex min-h-0 min-w-0 flex-1 flex-col items-center overflow-auto rounded-lg border border-neutral-200 bg-[#EFF0F2] py-[18px]', "data-testid": 'pdf-document-wrapper', children: [_jsx("p", { id: 'consent-overlay-kbd-help', className: 'nucleus-sr-only', children: translate('Tab moves between overlays on this page. Arrow keys move the overlay. Shift plus arrow keys resize. Page Up and Page Down move it to another page. Enter or Space opens Configure. Delete or Backspace removes the overlay.') }), _jsx("span", { role: 'status', className: 'nucleus-sr-only', "data-testid": 'consent-overlay-announcement', children: announcement }), _jsx(Document, { file: document.pdfDataUrl, onLoadSuccess: onDocumentLoadSuccess, options: pdfOptions, loading: _jsx(Spinner, { wrapper: 'full' }), "data-testid": 'pdf-document', className: 'flex flex-col items-center gap-[18px]', children: pageNumbers.map((pageNumber) => (_jsx("div", { ref: (node) => {
                                        if (node) {
                                            pageRefs.current.set(pageNumber, node);
                                            // Document renders these boxes after the load callback, so measure once they exist.
                                            requestAnimationFrame(updateViewport);
                                        }
                                        else {
                                            pageRefs.current.delete(pageNumber);
                                        }
                                    }, className: 'relative bg-white shadow-[0px_2px_12px_rgba(0,0,0,0.12)]', "data-testid": `pdf-page-wrapper-${pageNumber}`, style: {
                                        cursor: activePlacementType ? 'crosshair' : 'default',
                                        width: pageWidth,
                                        // Overlays and placement are positioned against this box, so once a page renders the
                                        // box must be exactly the page's size, not the placeholder's.
                                        height: mountedPages.has(pageNumber) ? undefined : placeholderHeight,
                                    }, onClick: (e) => handlePlaceElement(pageNumber, e), children: mountedPages.has(pageNumber) ? (_jsxs(_Fragment, { children: [_jsx(Page, { pageNumber: pageNumber, width: pageWidth, 
                                                // Holds the slot open while a freshly mounted page loads, so the pages below don't jump.
                                                loading: _jsx("div", { className: 'flex items-center justify-center', style: { height: placeholderHeight }, children: _jsx(Spinner, {}) }), 
                                                // A rendered page replaces its placeholder height, which shifts later pages.
                                                onRenderSuccess: updateViewport, onLoadSuccess: pageNumber === 1
                                                    ? ({ originalWidth, originalHeight }) => setPageAspect(originalHeight / originalWidth)
                                                    : undefined }), _jsx("div", { className: readOnly ? 'pointer-events-none [&_*]:!pointer-events-none' : undefined, ...(readOnly ? { inert: true } : {}), children: _jsx(ConsentElementOverlay, { pageNumber: pageNumber, totalPages: totalPages, elements: document.elements, selectedElementId: readOnly ? null : (selectedElement?.id ?? null), onUpdateElement: handleUpdateElement, onUpdateElementPosition: handleUpdateElementPosition, onDeleteElement: handleDeleteElement, onConfigureElement: (element) => setSelectedElement(element.id), onSelectElement: setSelectedElement, onAnnounce: setAnnouncement, focusedItemId: focusedOverlayItemId, onFocusItem: setFocusedOverlayItemId }) })] })) : null }, pageNumber))) })] }), !readOnly && selectedElement ? (_jsx(ConsentPlacementInspector, { element: selectedElement, onUpdateElement: handleUpdateElement, onClose: () => setSelectedElement(null) })) : null] })] }));
};
